import { Link, useNavigate } from 'react-router-dom';
import type { Banner } from '../api/types';
import { rememberCatalogPosition } from '../lib/gameLaunch';

interface Props {
  cards: Banner[];
  loading?: boolean;
}

function resolveHref(card: Banner): string | null {
  if (card.action_type === 'none') return null;
  if (card.action_type === 'open_game' && card.game_public_id) {
    return `/play/${card.game_public_id}`;
  }
  if (card.action_type === 'campaign_info') {
    return `/campaigns/${card.slug}`;
  }
  if (card.link_url?.startsWith('/')) return card.link_url;
  return null;
}

export function CampaignCards({ cards, loading }: Props) {
  const navigate = useNavigate();
  const items = cards.filter((c) => (c.banner_type || 'campaign') === 'campaign');

  if (loading) {
    return (
      <div className="campaign-rail" aria-hidden="true">
        {[0, 1, 2, 3].map((n) => (
          <div key={n} className="campaign-card skeleton" />
        ))}
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div className="campaign-rail" role="list">
      {items.map((card) => {
        const href = resolveHref(card);
        const body = (
          <>
            {card.image_url ? (
              <img src={card.image_url} alt="" className="campaign-card-art" loading="lazy" decoding="async" />
            ) : (
              <span className="campaign-card-fallback" aria-hidden="true" />
            )}
            <span className="campaign-card-label">{card.title || card.slug}</span>
          </>
        );

        if (!href) {
          return (
            <div key={card.id || card.slug} className="campaign-card" role="listitem">
              {body}
            </div>
          );
        }

        if (href.startsWith('/play/')) {
          return (
            <button
              key={card.id || card.slug}
              type="button"
              className="campaign-card campaign-card-btn"
              role="listitem"
              onClick={() => {
                rememberCatalogPosition('/');
                navigate(href);
              }}
            >
              {body}
            </button>
          );
        }

        return (
          <Link key={card.id || card.slug} to={href} className="campaign-card" role="listitem">
            {body}
          </Link>
        );
      })}
    </div>
  );
}
